/**
 * useTelegramMiniApp Hook
 *
 * Detects if the app is running inside Telegram Mini App WebView.
 * Provides access to Telegram WebApp API and initData for authentication.
 *
 * @example
 * ```tsx
 * const { isMiniApp, isReady, initData, webApp } = useTelegramMiniApp();
 *
 * useEffect(() => {
 *   if (isMiniApp && isReady && initData) {
 *     // Authenticate with initData
 *     authenticateWithTelegram(initData);
 *   }
 * }, [isMiniApp, isReady, initData]);
 * ```
 */

'use client';

import { useEffect, useState, useCallback } from 'react';
import { logger } from '@/shared/infrastructure/logging/logger';

/**
 * Telegram WebApp interface (subset of full API)
 * @see https://core.telegram.org/bots/webapps#initializing-mini-apps
 */
export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    query_id?: string;
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
      photo_url?: string;
    };
    auth_date: number;
    hash: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  ready: () => void;
  expand: () => void;
  close: () => void;
  openLink: (url: string) => void;
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    isProgressVisible: boolean;
    setText: (text: string) => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  BackButton: {
    isVisible: boolean;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

export interface UseTelegramMiniAppResult {
  /** True if running inside Telegram Mini App */
  isMiniApp: boolean;
  /** True when WebApp.ready() has been called */
  isReady: boolean;
  /** Raw initData string for server validation */
  initData: string | null;
  /** Telegram WebApp instance (null if not in Mini App) */
  webApp: TelegramWebApp | null;
  /** User's preferred language from Telegram */
  languageCode: string | null;
}

/**
 * Detect and initialize Telegram Mini App
 *
 * Call this hook in your root component to:
 * 1. Detect if running in Telegram
 * 2. Initialize the WebApp
 * 3. Get initData for authentication
 */
export function useTelegramMiniApp(): UseTelegramMiniAppResult {
  const [isMiniApp, setIsMiniApp] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [initData, setInitData] = useState<string | null>(null);
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
  const [languageCode, setLanguageCode] = useState<string | null>(null);

  useEffect(() => {
    // Only inside a real Telegram WebView — in a plain browser the SDK spams
    // the console with postEvent noise and does nothing useful. Detection
    // WITHOUT the SDK: native clients inject TelegramWebviewProxy; Telegram
    // Web passes tgWebApp* hash params; the SDK persists init params in
    // sessionStorage across in-app navigations.
    const inTelegramWebView =
      typeof (window as { TelegramWebviewProxy?: unknown }).TelegramWebviewProxy !== 'undefined'
      || window.location.hash.includes('tgWebApp')
      || (() => {
        try {
          const params = sessionStorage.getItem('__telegram__initParams');
          if (!params) return false;
          const parsed = JSON.parse(params) as Record<string, unknown>;
          // The SDK writes this key even OUTSIDE Telegram (empty values) —
          // only non-empty init data proves a real WebView session.
          return !!(parsed.tgWebAppData || parsed.tgWebAppPlatform);
        } catch {
          return false;
        }
      })();
    if (!inTelegramWebView) {
      setIsReady(true);
      return;
    }

    // Load Telegram WebApp SDK dynamically
    function loadTelegramSDK(): Promise<void> {
      return new Promise((resolve, reject) => {
        // Already loaded
        if (window.Telegram?.WebApp) {
          resolve();
          return;
        }

        // Check if script already exists
        const existingScript = document.querySelector('script[src*="telegram-web-app.js"]');
        if (existingScript) {
          existingScript.addEventListener('load', () => resolve());
          existingScript.addEventListener('error', reject);
          return;
        }

        // Create and load script
        const script = document.createElement('script');
        script.src = 'https://telegram.org/js/telegram-web-app.js';
        script.async = false;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Telegram SDK'));
        document.head.appendChild(script);
      });
    }

    async function init() {
      try {
        await loadTelegramSDK();

        const tg = window.Telegram?.WebApp;

        if (tg && tg.initData) {
          setIsMiniApp(true);
          setWebApp(tg);
          setInitData(tg.initData);

          if (tg.initDataUnsafe?.user?.language_code) {
            setLanguageCode(tg.initDataUnsafe.user.language_code);
          }

          tg.ready();
          tg.expand();
        }
      } catch (error) {
        logger.error('[useTelegramMiniApp] SDK load error:', error);
      } finally {
        setIsReady(true);
      }
    }

    init();
  }, []);

  return {
    isMiniApp,
    isReady,
    initData,
    webApp,
    languageCode,
  };
}

/**
 * Hook for using Telegram haptic feedback
 * Returns no-op functions if not in Mini App
 */
function useTelegramHaptics() {
  const { webApp } = useTelegramMiniApp();

  const impact = useCallback(
    (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'medium') => {
      webApp?.HapticFeedback?.impactOccurred(style);
    },
    [webApp]
  );

  const notification = useCallback(
    (type: 'error' | 'success' | 'warning') => {
      webApp?.HapticFeedback?.notificationOccurred(type);
    },
    [webApp]
  );

  const selection = useCallback(() => {
    webApp?.HapticFeedback?.selectionChanged();
  }, [webApp]);

  return { impact, notification, selection };
}
